import { Identifier } from "../models";
import { StringSanitizer } from "../utils/StringSanitizer";

const themalijst = require("../assets/waardelijsten/themas.json");
const talenlijst = require("../assets/waardelijsten/talen.json");
const redenlijst = require("../assets/waardelijsten/ccw_plooi_reden_intrekking_of_vervanging_1.json");
const ministerielijst = require("../assets/waardelijsten/rwc_ministeries_compleet_6.json");
const overigeOrganisatieslijst = require("../assets/waardelijsten/rwp_overige_overheidsorganisaties_peildatum_11.json");
const samenwerkingsOrganisatieslijst = require("../assets/waardelijsten/rwp_samenwerkingsorganisaties_peildatum_2.json");
const gemeentenOrganisatieslijst = require("../assets/waardelijsten/rwp_gemeenten_peildatum_4.json");
const provinciesOrganisatieslijst = require("../assets/waardelijsten/rwp_provincies_peildatum_1.json");
const waterschappenOrganisatieslijst = require("../assets/waardelijsten/rwp_waterschappen_peildatum_1.json");
const zelfstandigeBestuurorganenlijst = require("../assets/waardelijsten/rwp_zbo_peildatum_5.json");
const documentsoortenlijstKST = require("../assets/waardelijsten/documentsoorten.json");
const documentsoortenlijst = require("../assets/waardelijsten/ccw_plooi_documentsoorten_6.json");
const documenthandelingenlijst = require("../assets/waardelijsten/documenthandelingen.json");
const informatiecategorieen = require("../assets/waardelijsten/scw_woo_informatiecategorieen_4.json");

export interface IdentifierWithChildren extends Identifier {
  children?: IdentifierWithChildren[];
}

export interface WaardelijstItem {
  item: Identifier;
  children?: WaardelijstItem[];
}

export class WaardelijstFactory {
  private readonly stringSanitizer: StringSanitizer;

  constructor() {
    this.stringSanitizer = new StringSanitizer();
  }

  private readonly mapWaardelijstItem = (item: any, ctx: any): IdentifierWithChildren => ({
    id: this.stringSanitizer.sanitizeLdId(item, ctx),
    label: this.stringSanitizer.sanitizeLdPrefLabel(item, "prefLabel"),
  });

  private readonly traverseTree = (data: any[], ctx: any): IdentifierWithChildren[] => {
    const items = [...data];
    let tree = items.map((item: any) => this.mapWaardelijstItem(item, ctx));

    items.forEach((originalItem: any) => {
      const mappedItem = this.mapWaardelijstItem(originalItem, ctx);

      if (!originalItem.broader) return;

      const parent = tree.find(
        (iwc: IdentifierWithChildren) => iwc.id === this.stringSanitizer.sanitizeLdId(originalItem["broader"], ctx)
      );

      if (!parent) return;

      if (!parent.children && !this.stringSanitizer.extractIdentifierFromPid(parent.id).startsWith("k_"))
        parent.children = [{ id: parent.id, label: parent.label }];
      else parent.children ??= [];

      parent.children.push(mappedItem);
      tree = tree.filter((iwc: IdentifierWithChildren) => iwc.id !== mappedItem.id);
    });

    return tree.filter((item: IdentifierWithChildren) => item.id && item.label && item.label !== "TOP-lijst");
  };

  private readonly getFlatSkosTree = (
    waardelijst: any[],
    labelIdentifier: string,
    invalidIdentifier?: string
  ): IdentifierWithChildren[] => {
    return waardelijst
      .map((waardelijstItem: { [x: string]: { [x: string]: string }[] }) => {
        if (
          !waardelijstItem.hasOwnProperty("@id") ||
          !waardelijstItem.hasOwnProperty(labelIdentifier) ||
          (invalidIdentifier && waardelijstItem.hasOwnProperty(invalidIdentifier))
        )
          return undefined;

        const item: any = { id: waardelijstItem["@id"] };
        if (waardelijstItem.hasOwnProperty(labelIdentifier))
          item.label = waardelijstItem[labelIdentifier][0]["@value"] ?? undefined;

        return item;
      })
      .filter((item: any) => item !== undefined);
  };

  private readonly getSkosTree = (waardelijst: any[], tree: IdentifierWithChildren[]) => {
    const markedForDelete: string[] = [];

    waardelijst.forEach((waardelijstItem: any) => {
      const itemId = waardelijstItem["@id"];

      const parent = waardelijst.find((i) => {
        const members = i["http://www.w3.org/2004/02/skos/core#member"];
        return members?.some((member: any) => member["@id"] === waardelijstItem["@id"]);
      });

      //Zelf parent
      if (!parent) return;

      const parentId = parent["@id"];
      const parentInTree = tree.find((iwc: IdentifierWithChildren) => iwc.id === parentId);
      const childInTree = tree.find((iwc: IdentifierWithChildren) => iwc.id === itemId);

      if (!parentInTree || !childInTree) return;

      parentInTree.children ??= [];

      parentInTree.children.push(childInTree);
      markedForDelete.push(childInTree.id);
    });

    let newTree = tree
      .filter((iwc: IdentifierWithChildren) => markedForDelete.every((mark: string) => mark !== iwc.id))
      .filter((item: IdentifierWithChildren) => item.children && item.children.length > 0);

    return newTree;
  };

  private readonly traverseTreeSkos = (
    waardelijst: any[],
    labelIdentifier: string,
    invalidIdentifier: string
  ): IdentifierWithChildren[] => {
    const allParentItems = waardelijst.filter((list: { hasOwnProperty: (arg0: string) => any }) =>
      list.hasOwnProperty("http://www.w3.org/2004/02/skos/core#member")
    );

    let tree: IdentifierWithChildren[] = this.getFlatSkosTree(waardelijst, labelIdentifier, invalidIdentifier);

    if (allParentItems.length === 0) return tree;

    let newTree = this.getSkosTree(waardelijst, tree);

    if (newTree.length === 1) {
      const topParentSelectableChildren = newTree[0].children!.every((iwc: IdentifierWithChildren) => iwc.children);
      if (newTree[0].children && topParentSelectableChildren) {
        newTree = newTree[0].children;
      } else {
        newTree[0].label = "";
      }
    }

    return newTree;
  };

  private readonly findElement = (item: any, tree: IdentifierWithChildren[]): IdentifierWithChildren | undefined => {
    const parent = tree.find((iwc: IdentifierWithChildren) => {
      if (iwc.id === item) return iwc;
      if (iwc.children) return this.findElement(item, iwc.children);
      return undefined;
    });
    return parent;
  };

  private readonly SkosBroader = (waardelijst: any[]): IdentifierWithChildren[] => {
    const labelIdentifier = "http://www.w3.org/2004/02/skos/core#prefLabel";
    const items: any[] = [...waardelijst];
    let tree: IdentifierWithChildren[] = this.getFlatSkosTree(waardelijst, labelIdentifier);
    let removeFromTree: string[] = [];

    items.forEach((waardelijstItem: any) => {
      const broader = waardelijstItem["http://www.w3.org/2004/02/skos/core#broader"];
      if (!broader) return;

      const parent: IdentifierWithChildren | undefined = this.findElement(broader[0]["@id"], tree);
      if (!parent) return;
      if (!parent.children && !this.stringSanitizer.extractIdentifierFromPid(parent.id).startsWith("k_"))
        parent.children = [{ id: parent.id, label: parent.label }];
      else parent.children ??= [];

      const treeItem = this.findElement(waardelijstItem["@id"], tree);
      parent.children.push(treeItem!);
      removeFromTree.push(treeItem!.id);
    });

    tree = tree.filter((item: IdentifierWithChildren) => !removeFromTree.includes(item.id));
    return tree;
  };

  private readonly UriLabelLijst = (jsonLijst: any[]) => {
    return jsonLijst.map((lijstItem: any) => {
      return { id: lijstItem.uri, label: lijstItem.label };
    });
  };

  private readonly graphContextlijst = (jsonLijst: any): IdentifierWithChildren[] => {
    const data = jsonLijst["@graph"];
    const context = jsonLijst["@context"];
    return this.traverseTree(data, context);
  };

  private readonly skosLijst = (jsonLijst: any[], invalid: boolean = true): IdentifierWithChildren[] => {
    const prefLabel = "http://www.w3.org/2004/02/skos/core#prefLabel";
    const rdfLabel = "http://www.w3.org/2000/01/rdf-schema#label";
    const invalidlabel = "http://www.w3.org/ns/prov#invalidatedAtTime";

    if (jsonLijst.length === 0) return [];

    let labelIdentifier = "";

    if (jsonLijst[0].hasOwnProperty(prefLabel)) labelIdentifier = prefLabel;
    else if (jsonLijst[0].hasOwnProperty(rdfLabel)) labelIdentifier = rdfLabel;

    const lijst = this.traverseTreeSkos(jsonLijst, labelIdentifier, invalid ? invalidlabel : "");
    return lijst;
  };

  public createThemaLijst = () => this.graphContextlijst(themalijst);
  public createTaalLijst = () => this.skosLijst(talenlijst);
  public createRedenLijst = () => this.skosLijst(redenlijst);
  public createMinisterieLijst = (invalid: boolean = true) => this.skosLijst(ministerielijst, invalid);
  public createOverigeOrganisatiesLijst = (invalid: boolean = true) =>
    this.skosLijst(overigeOrganisatieslijst, invalid);
  public createSamenwerkingsOrganisatiesLijst = (invalid: boolean = true) =>
    this.skosLijst(samenwerkingsOrganisatieslijst, invalid);
  public createZelfstandigeBestuurorganenLijst = (invalid: boolean = true) =>
    this.skosLijst(zelfstandigeBestuurorganenlijst, invalid);
  public createGemeentenOrganisatiesLijst = (invalid: boolean = true) =>
    this.skosLijst(gemeentenOrganisatieslijst, invalid);
  public createProvinciesOrganisatiesLijst = (invalid: boolean = true) =>
    this.skosLijst(provinciesOrganisatieslijst, invalid);
  public createWaterschappenOrganisatiesLijst = (invalid: boolean = true) =>
    this.skosLijst(waterschappenOrganisatieslijst, invalid);

  public createFromTooi = (payload: any) => this.UriLabelLijst(payload);
  public createDocumenthandelingenLijst = () => this.skosLijst(documenthandelingenlijst);
  public createInformatiecategorieenLijst = () => this.skosLijst(informatiecategorieen);
  public createDocumentsoortenLijst = () => this.SkosBroader(documentsoortenlijst);
  public createDocumentsoortenLijstKST = (isKST: boolean = false) => {
    const items = this.skosLijst(documentsoortenlijstKST);
    const itemWithKamerstuk = items[0].children?.find(
      (iwc: IdentifierWithChildren) => iwc.label === "parlementair document"
    );
    if (itemWithKamerstuk && !isKST)
      itemWithKamerstuk.children = itemWithKamerstuk.children?.filter(
        (iwc: IdentifierWithChildren) => iwc.label !== "Kamerstuk"
      );

    return items;
  };
}
